import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Globe, Building2, FlaskConical, Smartphone, Tablet, Monitor,
  Languages, CheckCircle, AlertCircle, Loader2, ChevronDown, FileText
} from 'lucide-react';
import api from '../../services/api';
import { ToggleRow } from '../ui';

/**
 * Primary/Variant Assessment Creation Modal
 * Allows users to:
 * 1. Create Primary assessment (default US-English, Web)
 * 2. Create Variant (select primary, device, language)
 */
const PrimaryVariantModal = ({ 
  isOpen, 
  onClose, 
  onCreatePrimary, 
  onCreateVariant, 
  existingPrimaries = [],
  preselectedPrimary = null,
  preselectedDeviceId = null
}) => {
  const [mode, setMode] = useState(preselectedPrimary ? 'variant' : 'primary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState({ phone: [], tablet: [], desktop: [] });
  const [loadingDevices, setLoadingDevices] = useState(true);

  // Primary form state
  const [primaryForm, setPrimaryForm] = useState({
    name: '',
    description: '',
    library_type: 'sponsor',
    assessment_type: 'fixed',
    language: 'en-US',
    default_device_id: null
  });

  // Variant form state
  const [variantForm, setVariantForm] = useState({
    primary_assessment_id: preselectedPrimary?.id || '',
    primary_assessment_uuid: preselectedPrimary?.uuid || '',
    device_id: preselectedDeviceId || '',
    language: '',
    variant_name: '',
    copy_structure: true
  });

  // Fetch devices on mount
  useEffect(() => {
    if (isOpen) {
      fetchDevices();
    }
  }, [isOpen]);
  
  // Update form when preselected values change
  useEffect(() => {
    if (isOpen && preselectedPrimary) {
      setMode('variant');
      setVariantForm(prev => ({
        ...prev,
        primary_assessment_id: preselectedPrimary.id,
        primary_assessment_uuid: preselectedPrimary.uuid,
        device_id: preselectedDeviceId || prev.device_id
      }));
    }
  }, [isOpen, preselectedPrimary, preselectedDeviceId]);

  const fetchDevices = async () => {
    setLoadingDevices(true);
    try {
      const response = await api.get('/api/assessment-builder/devices/');
      const data = response.data;
      setDevices(data.grouped || { phone: [], tablet: [], desktop: [] });
      
      // Set default device to first Web Desktop for primaries
      const webDesktop = data.grouped.desktop?.find(d => d.name.includes('Web Desktop'));
      if (webDesktop) {
        setPrimaryForm(prev => ({ ...prev, default_device_id: webDesktop.id }));
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  };

  const handlePrimarySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onCreatePrimary(primaryForm);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create primary assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleVariantSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onCreateVariant(variantForm);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create variant');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPrimaryForm({
      name: '',
      description: '',
      library_type: 'sponsor',
      assessment_type: 'fixed',
      language: 'en-US',
      default_device_id: null
    });
    setVariantForm({
      primary_assessment_id: '',
      primary_assessment_uuid: '',
      device_id: '',
      language: '',
      variant_name: '',
      copy_structure: true
    });
    setError('');
    setMode('primary');
    onClose();
  };

  if (!isOpen) return null;

  const libraryOptions = [
    { value: 'vpg', label: 'VPG Global Library', icon: Globe, color: 'text-gray-400 dark:text-gray-500' },
    { value: 'sponsor', label: 'Sponsor Library', icon: Building2, color: 'text-gray-400 dark:text-gray-500' },
    { value: 'study', label: 'Study-Specific Library', icon: FlaskConical, color: 'text-gray-400 dark:text-gray-500' }
  ];

  const assessmentTypes = [
    { value: 'fixed', label: 'Fixed Form' },
    { value: 'adaptive', label: 'Adaptive (CAT)' },
    { value: 'item_bank', label: 'Item Library' }
  ];

  const commonLanguages = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'es-ES', label: 'Spanish (Spain)' },
    { code: 'es-MX', label: 'Spanish (Mexico)' },
    { code: 'fr-FR', label: 'French (France)' },
    { code: 'de-DE', label: 'German (Germany)' },
    { code: 'it-IT', label: 'Italian (Italy)' },
    { code: 'pt-BR', label: 'Portuguese (Brazil)' },
    { code: 'ja-JP', label: 'Japanese (Japan)' },
    { code: 'zh-CN', label: 'Chinese (Simplified)' },
    { code: 'ko-KR', label: 'Korean (South Korea)' }
  ];

  // Footer band that stays in view while the form scrolls (FECOA-2174)
  const footerClass = 'sticky bottom-0 -mx-6 -mb-5 flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-6 py-3.5 dark:border-white/[0.06] dark:bg-gray-800';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Create Assessment</h2>
            <button
              onClick={handleClose}
              className="btn btn-ghost btn-sm btn-icon -mr-2"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="border-b border-gray-100 px-6 py-3 dark:border-white/[0.06]">
            <div className="segmented flex w-full">
              <button
                onClick={() => setMode('primary')}
                className={`segmented-item flex-1 justify-center ${
                  mode === 'primary'
                    ? 'segmented-item-active'
                    : ''
                }`}
              >
                <FileText className="h-4 w-4" />
                Create Primary
              </button>
              <button
                onClick={() => setMode('variant')}
                className={`segmented-item flex-1 justify-center ${
                  mode === 'variant'
                    ? 'segmented-item-active'
                    : ''
                }`}
              >
                <Languages className="h-4 w-4" />
                Create Variant
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {error && (
              <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3.5 dark:border-red-500/30 dark:bg-red-500/10">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
                <p className="text-[13px] text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {mode === 'primary' ? (
              <form onSubmit={handlePrimarySubmit} className="space-y-5">
                {/* Assessment Name */}
                <div>
                  <label className="label">
                    Assessment Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={primaryForm.name}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, name: e.target.value })}
                    placeholder="e.g., PHQ-9 Depression Scale"
                    className="input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="label">
                    Description
                  </label>
                  <textarea
                    value={primaryForm.description}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, description: e.target.value })}
                    placeholder="Brief description of this assessment..."
                    rows={3}
                    className="input resize-none"
                  />
                </div>

                {/* Library Type */}
                <div>
                  <label className="label">
                    Library Type *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {libraryOptions.map((option) => {
                      const Icon = option.icon;
                      const isSelected = primaryForm.library_type === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setPrimaryForm({ ...primaryForm, library_type: option.value })}
                          className={`rounded-xl border p-4 transition-colors ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                              : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
                          }`}
                        >
                          <Icon className={`mx-auto mb-2 h-5 w-5 ${isSelected ? 'text-primary-600 dark:text-primary-400' : option.color}`} />
                          <div className="text-[13px] font-medium text-gray-900 dark:text-gray-50">{option.label.split(' ')[0]}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Assessment Type */}
                <div>
                  <label className="label">
                    Assessment Type *
                  </label>
                  <select
                    value={primaryForm.assessment_type}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, assessment_type: e.target.value })}
                    className="input"
                  >
                    {assessmentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Language */}
                <div>
                  <label className="label">
                    Default Language *
                  </label>
                  <select
                    value={primaryForm.language}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, language: e.target.value })}
                    className="input"
                  >
                    {commonLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                  <p className="hint">Primary assessments typically use en-US</p>
                </div>

                {/* Default Device */}
                <div>
                  <label className="label">
                    Default Device
                  </label>
                  <select
                    value={primaryForm.default_device_id || ''}
                    onChange={(e) => setPrimaryForm({ ...primaryForm, default_device_id: parseInt(e.target.value) })}
                    className="input"
                  >
                    <option value="">Select device...</option>
                    <optgroup label="Desktop">
                      {devices.desktop?.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name} ({device.screen_width}×{device.screen_height})
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="hint">Typically Web Desktop for primary forms</p>
                </div>

                {/* Submit Button */}
                <div className={footerClass}>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !primaryForm.name}
                    className="btn btn-primary"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Create Primary
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVariantSubmit} className="space-y-5">
                <div className="flex items-start gap-2.5 rounded-lg border border-gray-200/70 bg-gray-50 p-3.5 text-[12.5px] leading-relaxed text-gray-600 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-gray-300">
                  <Languages className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  <p>
                    Variants copy the primary's structure and allow independent editing for specific languages and devices.
                  </p>
                </div>

                {/* Select Primary */}
                <div>
                  <label className="label">
                    Select Primary Assessment *
                  </label>
                  <select
                    required
                    value={variantForm.primary_assessment_id}
                    onChange={(e) => {
                      const selected = existingPrimaries.find(p => String(p.id) === e.target.value);
                      setVariantForm({ ...variantForm, primary_assessment_id: e.target.value, primary_assessment_uuid: selected?.uuid || '' });
                    }}
                    className="input"
                  >
                    <option value="">Choose a primary assessment...</option>
                    {existingPrimaries.map((primary) => (
                      <option key={primary.id} value={primary.id}>
                        {primary.name} (v{primary.version})
                      </option>
                    ))}
                  </select>
                  {existingPrimaries.length === 0 && (
                    <p className="mt-2 text-[13px] text-amber-700 dark:text-amber-300">
                      No primary assessments available. Create a primary first.
                    </p>
                  )}
                </div>

                {/* Select Device */}
                <div>
                  <label className="label">
                    Target Device *
                  </label>
                  {loadingDevices ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
                    </div>
                  ) : (
                    <select
                      required
                      value={variantForm.device_id}
                      onChange={(e) => setVariantForm({ ...variantForm, device_id: e.target.value })}
                      className="input"
                    >
                      <option value="">Select device...</option>
                      <optgroup label="Phones">
                        {devices.phone?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Tablets">
                        {devices.tablet?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Desktop">
                        {devices.desktop?.map((device) => (
                          <option key={device.id} value={device.id}>
                            {device.name} ({device.screen_width}×{device.screen_height})
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  )}
                </div>

                {/* Language */}
                <div>
                  <label className="label">
                    Language *
                  </label>
                  <select
                    required
                    value={variantForm.language}
                    onChange={(e) => setVariantForm({ ...variantForm, language: e.target.value })}
                    className="input"
                  >
                    <option value="">Select language...</option>
                    {commonLanguages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Variant Name (Optional) */}
                <div>
                  <label className="label">
                    Variant Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={variantForm.variant_name}
                    onChange={(e) => setVariantForm({ ...variantForm, variant_name: e.target.value })}
                    placeholder="Leave blank to auto-generate"
                    className="input"
                  />
                  <p className="hint">
                    If left blank, will auto-generate as: "{existingPrimaries.find(p => p.id == variantForm.primary_assessment_id)?.name || 'Primary'} - {variantForm.language} ({devices.phone?.concat(devices.tablet, devices.desktop)?.find(d => d.id == variantForm.device_id)?.name || 'Device'})"
                  </p>
                </div>

                {/* Copy Structure */}
                <ToggleRow
                  id="copy_structure"
                  title="Copy structure from primary"
                  description="Recommended: Copies all screens and questions. Uncheck to start with empty variant."
                  checked={variantForm.copy_structure}
                  onChange={(e) => setVariantForm({ ...variantForm, copy_structure: e.target.checked })}
                />

                {/* Submit Button */}
                <div className={footerClass}>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !variantForm.primary_assessment_id || !variantForm.device_id || !variantForm.language}
                    className="btn btn-primary"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating Variant...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Create Variant
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PrimaryVariantModal;
